import { api } from 'redux-store/api';
// types
import { setPaymentPlan } from 'redux-store/slices/billing-slice';
import {
  IBillingCustomerSecretBody,
  IBillingCustomerSecretResponse,
  IBillingPlan,
  ICancelSubscriptionBody,
  IChangeTerms,
  IChangeTermsArgs,
  IConfirmPaymentBody,
  ICurrentPaymentPlan,
  IPaymentMethod,
  IPaymentPlan,
  IPaymentPlanBodyData,
  IPaymentPlanUpgradeBody,
  IPaymentUpgrade,
  IPromoCodeBody,
  IReactivateSubscriptionArgs,
  IUpdatePaymentMethodBody,
  TConfirmPayment,
} from './billing.types';

enum Endpoints {
  BILLING = 'payments/plans',
  PAYMENT_METHODS = 'payments/payment-methods/',
}

export const billingApi = api.injectEndpoints({
  endpoints: (build) => ({
    createPaymentPlan: build.mutation<IBillingPlan, IPaymentPlanBodyData>({
      query: ({ data }) => ({
        url: `${Endpoints.BILLING}/`,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Company', 'Current Plan', 'User', 'Payment Methods', 'Billing History'],
      onQueryStarted: (_, { dispatch }) => {
        dispatch(setPaymentPlan({ paymentPlanId: null, couponCode: null }));
      },
    }),
    upgradePlan: build.mutation<IPaymentUpgrade, IPaymentPlanUpgradeBody>({
      query: ({ data }) => ({
        url: `${Endpoints.BILLING}/change`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['Current Plan'],
    }),
    confirmPayment: build.mutation<TConfirmPayment, IConfirmPaymentBody>({
      query: ({ data }) => ({
        url: `${Endpoints.BILLING}/current/confirm-payment`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['Current Plan'],
    }),
    getChangeTerms: build.query<IChangeTerms, IChangeTermsArgs>({
      query: ({ productId }) => ({
        url: `${Endpoints.BILLING}/current/get-change-terms/${productId}`,
      }),
    }),
    cancelSubscription: build.mutation<void, ICancelSubscriptionBody>({
      query: ({ data }) => ({
        url: `${Endpoints.BILLING}/cancel`,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Company', 'Current Plan'],
    }),
    getPlans: build.query<IPaymentPlan, void>({
      query: () => ({
        url: `${Endpoints.BILLING}`,
      }),
      providesTags: ['Payment Plans'],
    }),
    getCurrentPaymentPlan: build.query<ICurrentPaymentPlan, void>({
      query: () => ({
        url: `${Endpoints.BILLING}/current`,
      }),
      providesTags: ['Current Plan'],
    }),
    getPaymentMethods: build.query<IPaymentMethod, void>({
      query: () => ({
        url: `${Endpoints.PAYMENT_METHODS}`,
      }),
      providesTags: ['Payment Methods'],
    }),
    updatePaymentMethod: build.mutation<IPaymentMethod, IUpdatePaymentMethodBody>({
      query: ({ data }) => ({
        url: `${Endpoints.PAYMENT_METHODS}`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['Payment Methods'],
    }),
    deletePaymentMethod: build.mutation<IPaymentMethod, void>({
      query: () => ({
        url: `${Endpoints.PAYMENT_METHODS}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Payment Methods'],
    }),
    reactivateSubscription: build.mutation<ICurrentPaymentPlan, IReactivateSubscriptionArgs>({
      query: ({ data }) => ({
        url: `${Endpoints.BILLING}/current/reactivate`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['Current Plan'],
    }),
    applyPromoCode: build.mutation<void, IPromoCodeBody>({
      query: ({ data }) => ({
        url: `promo-code`,
        body: { code: data.code.toUpperCase() },
        method: 'POST',
      }),
      invalidatesTags: ['Current Plan', 'Payment Plans'],
    }),
    getCustomerSecret: build.mutation<IBillingCustomerSecretResponse, IBillingCustomerSecretBody>({
      query: ({ data }) => {
        return {
          url: `${Endpoints.BILLING}/billing-customer`,
          body: { email: data.email },
          method: 'POST',
        };
      },
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetCurrentPaymentPlanQuery,
  useGetPaymentMethodsQuery,
  useCreatePaymentPlanMutation,
  useUpgradePlanMutation,
  useGetChangeTermsQuery,
  useCancelSubscriptionMutation,
  useDeletePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useApplyPromoCodeMutation,
  useReactivateSubscriptionMutation,
  useConfirmPaymentMutation,
  useGetCustomerSecretMutation,
} = billingApi;
